// 60-second breed recommendation quiz.
// Each answer awards weighted points to each breed slug.
// The breed with the highest total is the primary recommendation.

export const quizQuestions = [
    {
        id: "region",
        title: "Where in Karnataka is your farm?",
        subtitle: "Climate shapes which breed will actually thrive.",
        options: [
            {
                id: "ghats",
                label: "Cool ghats",
                desc: "Chikmagalur, Hassan, Kodagu, Shivamogga",
                scores: { hf: 5, jersey: 4, "all-black": 2, gir: 2, kankrej: 1, hallikar: 2 },
            },
            {
                id: "plateau",
                label: "Central plateau",
                desc: "Mysuru, Mandya, Bengaluru rural, Tumakuru",
                scores: { hf: 3, jersey: 4, "all-black": 5, gir: 3, kankrej: 2, hallikar: 4 },
            },
            {
                id: "north",
                label: "North Karnataka",
                desc: "Bagalkot, Vijayapura, Raichur, Ballari, Kalaburagi",
                scores: { hf: 0, jersey: 2, "all-black": 2, gir: 5, kankrej: 5, hallikar: 3 },
            },
            {
                id: "malnad",
                label: "Malnad heartland",
                desc: "Mysuru, Chamarajanagar, Old Mysore belt",
                scores: { hf: 1, jersey: 3, "all-black": 3, gir: 4, kankrej: 2, hallikar: 5 },
            },
            {
                id: "unsure",
                label: "Not sure yet",
                desc: "Still deciding on location",
                scores: { hf: 2, jersey: 3, "all-black": 3, gir: 3, kankrej: 2, hallikar: 2 },
            },
        ],
    },
    {
        id: "goal",
        title: "What is your primary dairy goal?",
        subtitle: "Fluid milk, ghee, A2 or heritage — they demand different breeds.",
        options: [
            {
                id: "volume",
                label: "Maximum daily volume",
                desc: "Selling into fluid-milk cooperative contracts",
                scores: { hf: 5, jersey: 2, "all-black": 5, gir: 1, kankrej: 1, hallikar: 0 },
            },
            {
                id: "ghee",
                label: "Premium ghee & paneer",
                desc: "Value-added products with high fat & SNF",
                scores: { hf: 1, jersey: 5, "all-black": 3, gir: 5, kankrej: 3, hallikar: 2 },
            },
            {
                id: "a2",
                label: "A2 milk / organic premium",
                desc: "Branded direct-to-consumer at high margin",
                scores: { hf: 0, jersey: 2, "all-black": 0, gir: 5, kankrej: 3, hallikar: 3 },
            },
            {
                id: "household",
                label: "Household + side income",
                desc: "Family milk plus modest surplus sale",
                scores: { hf: 1, jersey: 3, "all-black": 2, gir: 3, kankrej: 4, hallikar: 5 },
            },
            {
                id: "heritage",
                label: "Heritage & conservation",
                desc: "Preserving Indian breeds and cultural farming",
                scores: { hf: 0, jersey: 0, "all-black": 0, gir: 4, kankrej: 5, hallikar: 5 },
            },
        ],
    },
    {
        id: "budget",
        title: "Your investment capacity per lactating cow?",
        subtitle: "Includes purchase, not shed or equipment.",
        options: [
            {
                id: "u50",
                label: "Under ₹50,000",
                desc: "Very small budget",
                scores: { hf: 0, jersey: 0, "all-black": 0, gir: 0, kankrej: 1, hallikar: 5 },
            },
            {
                id: "b50_80",
                label: "₹50k – ₹80k",
                desc: "Modest first purchase",
                scores: { hf: 1, jersey: 5, "all-black": 2, gir: 1, kankrej: 4, hallikar: 3 },
            },
            {
                id: "b80_100",
                label: "₹80k – ₹1,00,000",
                desc: "Serious first investment",
                scores: { hf: 3, jersey: 4, "all-black": 5, gir: 2, kankrej: 3, hallikar: 2 },
            },
            {
                id: "b100p",
                label: "₹1,00,000+ per cow",
                desc: "Premium genetics available",
                scores: { hf: 5, jersey: 3, "all-black": 4, gir: 5, kankrej: 3, hallikar: 2 },
            },
        ],
    },
    {
        id: "labour",
        title: "How much labour can you dedicate per cow, per day?",
        subtitle: "Includes feeding, cleaning, milking, records.",
        options: [
            {
                id: "under15",
                label: "Under 1.5 hours",
                desc: "Household-level care",
                scores: { hf: 0, jersey: 2, "all-black": 1, gir: 3, kankrej: 4, hallikar: 5 },
            },
            {
                id: "15_2",
                label: "1.5 – 2 hours",
                desc: "Small farm with focus",
                scores: { hf: 2, jersey: 5, "all-black": 3, gir: 5, kankrej: 4, hallikar: 3 },
            },
            {
                id: "2_25",
                label: "2 – 2.5 hours",
                desc: "Small-commercial setup",
                scores: { hf: 4, jersey: 4, "all-black": 5, gir: 3, kankrej: 2, hallikar: 1 },
            },
            {
                id: "over25",
                label: "2.5+ hours",
                desc: "Full commercial operation",
                scores: { hf: 5, jersey: 2, "all-black": 4, gir: 2, kankrej: 1, hallikar: 0 },
            },
        ],
    },
    {
        id: "experience",
        title: "Your dairy experience?",
        subtitle: "Honest answers give honest recommendations.",
        options: [
            {
                id: "beginner",
                label: "Complete beginner",
                desc: "First dairy purchase",
                scores: { hf: 0, jersey: 5, "all-black": 2, gir: 5, kankrej: 3, hallikar: 4 },
            },
            {
                id: "some",
                label: "Some hands-on experience",
                desc: "1–3 years around cattle",
                scores: { hf: 2, jersey: 4, "all-black": 4, gir: 4, kankrej: 4, hallikar: 3 },
            },
            {
                id: "experienced",
                label: "Experienced / commercial",
                desc: "Running or worked on a commercial dairy",
                scores: { hf: 5, jersey: 3, "all-black": 4, gir: 3, kankrej: 2, hallikar: 1 },
            },
        ],
    },
];

export function scoreQuiz(answers) {
    // answers = { region: 'ghats', goal: 'volume', ... }
    const totals = { hf: 0, jersey: 0, "all-black": 0, gir: 0, kankrej: 0, hallikar: 0 };
    for (const q of quizQuestions) {
        const chosen = answers[q.id];
        if (!chosen) continue;
        const opt = q.options.find((o) => o.id === chosen);
        if (!opt) continue;
        for (const [slug, pts] of Object.entries(opt.scores)) {
            totals[slug] = (totals[slug] || 0) + pts;
        }
    }
    const maxPossible = quizQuestions.length * 5;
    const ranked = Object.entries(totals)
        .map(([slug, points]) => ({ slug, points, match: Math.round((points / maxPossible) * 100) }))
        .sort((a, b) => b.points - a.points);
    return ranked;
}

export const quizIntro = {
    title: "Which cow suits you?",
    subtitle: "Five questions. Sixty seconds. A breed matched to your climate, market, budget and experience — with honest reasoning.",
};
